/* 147j+16da — Order Book Leaderboard tab.
 *
 * Ranks companies by consolidated order-flow over a rolling window
 * (default 60d), sortable by %TTM, absolute value, or # of wins.
 * Read-only. Zero LLM cost. Served to admin + subscribed users.
 *
 * Design notes:
 *  - Left-aligned dense table, not a card grid — traders scan lists.
 *  - Rank badge coloured by tier (top 5 gold, 6-10 silver, else muted).
 *  - Rows expand to show constituent filings (via /filings endpoint).
 *  - Filters are always visible above the table (no hidden drawer),
 *    matching the "no-friction dashboard" style used elsewhere.
 */
/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, Trophy, Info, HelpCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { v: 30,  l: '30d' },
  { v: 60,  l: '60d' },
  { v: 90,  l: '90d' },
  { v: 180, l: '6mo' },
];

const SORT_OPTIONS = [
  { v: 'best',     l: 'Best deals (composite)', short: 'best' },
  { v: 'pct_ttm',  l: '% of TTM revenue',       short: '%TTM' },
  { v: 'total_cr', l: 'Total order value',      short: '₹ Cr' },
  { v: 'n_orders', l: 'Number of wins',         short: '# wins' },
  { v: 'latest',   l: 'Most recent filing',     short: 'latest' },
];

const VERDICT_OPTIONS = [
  { v: 'ALL',  l: 'All',    tone: 'bg-zinc-500/20 text-zinc-200 border-zinc-500/50' },
  { v: 'BUY',  l: 'Buy',    tone: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50' },
  { v: 'HOLD', l: 'Hold',   tone: 'bg-amber-500/20 text-amber-200 border-amber-500/50' },
  { v: 'SKIP', l: 'Skip',   tone: 'bg-rose-500/20 text-rose-200 border-rose-500/50' },
];

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtCr = (v) => {
  if (v == null) return '—';
  if (v >= 10000) return `₹${(v / 1000).toFixed(1)}k Cr`;
  if (v >= 1000)  return `₹${v.toFixed(0)} Cr`;
  return `₹${v.toFixed(1)} Cr`;
};

const fmtPct = (v) => {
  if (v == null) return '—';
  if (v >= 1000) return `${(v / 100).toFixed(1)}k%`;
  if (v >= 100)  return `${v.toFixed(0)}%`;
  return `${v.toFixed(1)}%`;
};

// Colour a %TTM cell like a heat map — bigger = hotter.
const pctTone = (v) => {
  if (v == null) return 'text-zinc-400';
  if (v >= 200) return 'text-fuchsia-300 font-bold';
  if (v >= 100) return 'text-rose-300 font-semibold';
  if (v >= 50)  return 'text-amber-300 font-semibold';
  if (v >= 20)  return 'text-emerald-300';
  return 'text-zinc-300';
};

const rankBadge = (r) => {
  if (r === 1) return 'bg-amber-500/20 text-amber-300 border-amber-500/50 font-bold';
  if (r <= 3)  return 'bg-amber-500/10 text-amber-200 border-amber-500/30 font-semibold';
  if (r <= 10) return 'bg-sky-500/10 text-sky-300 border-sky-500/30 font-semibold';
  return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30';
};

const verdictTone = (v) => {
  const s = (v || '').toUpperCase();
  if (s === 'BUY')  return 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10';
  if (s === 'HOLD') return 'text-amber-300 border-amber-500/40 bg-amber-500/10';
  if (s === 'SKIP' || s === 'SELL') return 'text-rose-300 border-rose-500/40 bg-rose-500/10';
  return 'text-zinc-400 border-zinc-500/30 bg-zinc-500/10';
};

const tailwindTone = (t) => {
  if (t === 'STRONG')  return 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10';
  if (t === 'WEAK')    return 'text-rose-300 border-rose-500/40 bg-rose-500/10';
  if (t === 'NEUTRAL') return 'text-zinc-400 border-zinc-500/40 bg-zinc-500/10';
  return 'text-zinc-600 border-zinc-500/20 bg-zinc-500/5';
};

// ─── Expandable filings row ───────────────────────────────────────
const FilingsRow = ({ symbol, windowDays }) => {
  const [filings, setFilings] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(
          `${API}/api/admin/lodr/leaderboard/${encodeURIComponent(symbol)}/filings?window_days=${windowDays}`,
          { headers: authHeader() },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setFilings(d.filings || []);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [symbol, windowDays]);

  return (
    <tr className="bg-zinc-950/40" data-testid={`leaderboard-filings-${symbol}`}>
      <td colSpan={9} className="px-4 py-3">
        {loading && (
          <div className="text-[11px] text-zinc-500">Loading filings…</div>
        )}
        {error && (
          <div className="text-[11px] text-rose-400">Error: {error}</div>
        )}
        {filings && filings.length === 0 && (
          <div className="text-[11px] text-zinc-500">No individual filings found in this window.</div>
        )}
        {filings && filings.length > 0 && (
          <div className="border border-border/40 rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-zinc-900/60 text-zinc-400 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold">Date</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Value</th>
                  <th className="text-right px-3 py-1.5 font-semibold">%TTM</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Customer</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Verdict</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Headline</th>
                </tr>
              </thead>
              <tbody>
                {filings.map((f) => (
                  <tr key={f._id} className="border-t border-border/30">
                    <td className="px-3 py-1.5 text-zinc-400 font-mono">
                      {(f.announcement_date || '').slice(0, 10)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-200">
                      {fmtCr(f.order_value_cr)}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono ${pctTone(f.value_pct_of_ttm)}`}>
                      {fmtPct(f.value_pct_of_ttm)}
                    </td>
                    <td className="px-3 py-1.5 text-zinc-300 max-w-[220px] truncate">
                      {f.customer || <span className="text-zinc-600 italic">—</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      {f.verdict && (
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${verdictTone(f.verdict)}`}>
                          {f.verdict}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-zinc-400 max-w-[400px] truncate">
                      {f.attachment_url ? (
                        <a
                          href={f.attachment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-sky-300 hover:underline"
                        >
                          {f.headline}
                        </a>
                      ) : (
                        f.headline
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
};

// ─── Main component ───────────────────────────────────────────────
const OrderBookLeaderboardTab = () => {
  const [windowDays, setWindowDays] = useState(60);
  const [minTtm, setMinTtm] = useState(200);
  const [sortBy, setSortBy] = useState('best');
  const [verdictFilter, setVerdictFilter] = useState('ALL');
  const [showHelp, setShowHelp] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSymbol, setExpandedSymbol] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        window_days:     String(windowDays),
        min_ttm_cr:      String(minTtm),
        sort_by:         sortBy,
        verdict_filter:  verdictFilter,
        limit:           '50',
      });
      const r = await fetch(
        `${API}/api/admin/lodr/leaderboard?${qs.toString()}`,
        { headers: authHeader() },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.rows || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [windowDays, minTtm, sortBy, verdictFilter]);

  useEffect(() => { load(); }, [load]);

  const totalWins = useMemo(
    () => rows.reduce((a, r) => a + (r.n_orders || 0), 0),
    [rows],
  );
  const totalValue = useMemo(
    () => rows.reduce((a, r) => a + (r.total_order_value_cr || 0), 0),
    [rows],
  );

  return (
    <div className="space-y-4" data-testid="order-book-leaderboard-tab">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <h1 className="text-lg font-semibold text-foreground">
              Order Book Leaderboard
            </h1>
          </div>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-2xl">
            Companies ranked by consolidated new-order flow in the selected
            window — sorted by{' '}
            <strong className="text-foreground">
              {SORT_OPTIONS.find((o) => o.v === sortBy)?.l}
            </strong>.
            Excludes cancellations, red-flagged deals, and already-deduped filings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp((s) => !s)}
            className="text-[11px] px-2.5 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5"
            data-testid="leaderboard-help-btn"
          >
            <HelpCircle className="w-3 h-3" />
            {showHelp ? 'Hide guide' : 'How to read'}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="leaderboard-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── How to read guide (collapsible) ── */}
      {showHelp && (
        <div
          className="border border-sky-500/30 bg-sky-500/5 rounded p-4 space-y-2 text-[12px] text-zinc-300 leading-relaxed"
          data-testid="leaderboard-help-panel"
        >
          <div className="text-sky-300 font-semibold text-[13px]">
            How to read the Order Book Leaderboard
          </div>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>
              <strong className="text-foreground">Rank</strong> — position under the current sort. Gold badge = top 3, sky = top 10.
            </li>
            <li>
              <strong className="text-foreground">%TTM</strong> — total new-order value in the window ÷ company's trailing-12-month revenue.
              Higher = more forward revenue coverage. Colour-coded: fuchsia &gt; rose &gt; amber &gt; emerald.
            </li>
            <li>
              <strong className="text-foreground">Total ₹Cr</strong> — cumulative absolute order-book value in the window.
            </li>
            <li>
              <strong className="text-foreground">Wins</strong> — how many separate filings this company made. Higher = momentum / repeat customers.
            </li>
            <li>
              <strong className="text-foreground">Tailwind</strong> — the AI's read on the company's sector: STRONG (green), NEUTRAL, WEAK (red).
              A dash means we haven't ranked the sector yet (Minor rows skip this).
            </li>
            <li>
              <strong className="text-foreground">Verdict</strong> — the AI's call on the LATEST filing. BUY / HOLD / SKIP.
            </li>
            <li>
              <strong className="text-foreground">Score</strong> — a composite "best deals" number combining %TTM (capped 300),
              +40 for a BUY verdict, +3/win (max +15), +10 for STRONG tailwind, -5 for WEAK. Only shown when sorting by "Best deals".
            </li>
          </ul>
          <div className="pt-1 text-zinc-400">
            <strong className="text-foreground">Best usage:</strong> keep sort on{' '}
            <em>"Best deals"</em> and click <em>Verdict → Buy</em> to see only the
            AI's top conviction plays with real order momentum. Then expand any
            row to review the underlying filings before sizing up.
          </div>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap gap-3 items-end border border-border/40 rounded p-3 bg-surface/50">
        {/* Window */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Window</div>
          <div className="flex rounded overflow-hidden border border-border/50">
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.v}
                onClick={() => setWindowDays(o.v)}
                className={
                  'px-2.5 py-1 text-[11px] font-mono border-r border-border/50 last:border-r-0 ' +
                  (windowDays === o.v
                    ? 'bg-sky-500/20 text-sky-200 font-semibold'
                    : 'text-zinc-400 hover:bg-muted/30')
                }
                data-testid={`leaderboard-window-${o.v}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Sort by</div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-background border border-border/50 rounded px-2 py-1 text-[11px] text-foreground"
            data-testid="leaderboard-sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{o.l}</option>
            ))}
          </select>
        </div>

        {/* Verdict filter */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Verdict</div>
          <div className="flex rounded overflow-hidden border border-border/50">
            {VERDICT_OPTIONS.map((o) => (
              <button
                key={o.v}
                onClick={() => setVerdictFilter(o.v)}
                className={
                  'px-2.5 py-1 text-[11px] font-mono border-r border-border/50 last:border-r-0 ' +
                  (verdictFilter === o.v
                    ? o.tone + ' font-semibold'
                    : 'text-zinc-400 hover:bg-muted/30')
                }
                data-testid={`leaderboard-verdict-${o.v}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Min TTM */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
            Min TTM (₹ Cr)
            <span
              title="Hides nanocap noise — companies with a tiny revenue base can appear falsely high on %TTM just because the denominator is small. Default ₹200 Cr keeps smallcaps in, drops obvious junk."
              className="cursor-help"
            >
              <Info className="w-3 h-3 text-zinc-500" />
            </span>
          </div>
          <input
            type="number"
            value={minTtm}
            onChange={(e) => setMinTtm(Math.max(0, Number(e.target.value) || 0))}
            className="bg-background border border-border/50 rounded px-2 py-1 text-[11px] text-foreground w-24 font-mono"
            data-testid="leaderboard-minttm-input"
            min={0}
            step={50}
          />
        </div>

        {/* Summary chips */}
        <div className="ml-auto flex gap-3 text-[11px] text-muted-foreground">
          <div>
            <span className="text-zinc-500">Companies:</span>{' '}
            <span className="text-foreground font-semibold font-mono">{rows.length}</span>
          </div>
          <div>
            <span className="text-zinc-500">Total wins:</span>{' '}
            <span className="text-foreground font-semibold font-mono">{totalWins}</span>
          </div>
          <div>
            <span className="text-zinc-500">Cumulative:</span>{' '}
            <span className="text-foreground font-semibold font-mono">{fmtCr(totalValue)}</span>
          </div>
        </div>
      </div>

      {/* ── Error / loading ── */}
      {error && (
        <div className="border border-rose-500/40 bg-rose-500/10 text-rose-300 text-[12px] px-3 py-2 rounded">
          Failed to load leaderboard: {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="border border-border/40 rounded overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-muted/30 text-muted-foreground uppercase tracking-wider">
            <tr>
              <th className="w-12 text-center px-3 py-2 font-semibold">#</th>
              <th className="text-left px-3 py-2 font-semibold">Symbol</th>
              <th className="text-center px-3 py-2 font-semibold hidden md:table-cell" title="AI's read on the sector — STRONG / NEUTRAL / WEAK">Tailwind</th>
              <th className="text-right px-3 py-2 font-semibold" title="Total order value ÷ trailing-12m revenue">%TTM</th>
              <th className="text-right px-3 py-2 font-semibold">Total ₹Cr</th>
              <th className="text-right px-3 py-2 font-semibold">Wins</th>
              <th className="text-center px-3 py-2 font-semibold">Verdict</th>
              {sortBy === 'best' && (
                <th className="text-right px-3 py-2 font-semibold" title="Composite: %TTM (cap 300) +40 BUY, +3/win (cap 15), ±STRONG/WEAK tailwind">Score</th>
              )}
              <th className="text-right px-3 py-2 font-semibold hidden md:table-cell">TTM ₹Cr</th>
              <th className="text-left px-3 py-2 font-semibold hidden lg:table-cell">Latest</th>
              <th className="w-8 px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-8 text-zinc-500 text-[12px]">
                  Loading leaderboard…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-8 text-zinc-500 text-[12px]">
                  No companies match the current filters. Try widening the window,
                  changing the verdict filter to <em>All</em>, or lowering the Min TTM.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isOpen = expandedSymbol === r.symbol;
              return (
                <React.Fragment key={r.symbol || `bse-${r.bse_scrip_code}`}>
                  <tr
                    className="border-t border-border/30 hover:bg-muted/20 cursor-pointer"
                    onClick={() => setExpandedSymbol(isOpen ? null : r.symbol)}
                    data-testid={`leaderboard-row-${r.symbol}`}
                  >
                    <td className="text-center px-3 py-2">
                      <span className={`inline-block w-7 py-0.5 text-[10px] rounded border ${rankBadge(r.rank)}`}>
                        {r.rank}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <div className="font-mono font-semibold text-foreground">{r.symbol}</div>
                        {r.liquidity_tier && r.liquidity_tier !== 'NORMAL' && (
                          <span
                            className={
                              'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ' +
                              (r.liquidity_tier === 'GHOST'
                                ? 'bg-rose-500/20 text-rose-200 border-rose-400/60 animate-pulse'
                                : r.liquidity_tier === 'ILLIQUID'
                                ? 'bg-orange-500/15 text-orange-200 border-orange-400/50'
                                : 'bg-amber-500/10 text-amber-200 border-amber-500/40')
                            }
                            title={`${r.liquidity_tier} liquidity · ₹${(r.avg_daily_turnover_cr || 0).toFixed(2)} Cr avg daily turnover. Illiquid = higher manipulation risk.`}
                            data-testid={`leaderboard-liquidity-${r.symbol}`}
                          >
                            {r.liquidity_emoji} {r.liquidity_tier}
                          </span>
                        )}
                        {/* 147j+17w — Data-driven chip: color/label
                            tracks the BEST lead time we saw across
                            all Pre-LODR events for this symbol. */}
                        {r.pre_lodr_flag && (() => {
                          const leadH = r.pre_lodr_best_lead_hours;
                          const tier =
                            leadH == null ? 'MEDIA_LATE'
                              : leadH >= 24 ? 'PRE_LODR'
                              : leadH >= 6 ? 'MEDIA_EARLY'
                              : leadH >= 0 ? 'MEDIA_CONFIRM'
                              : 'MEDIA_LATE';
                          const style = {
                            PRE_LODR:      { cls: 'bg-amber-500/15 text-amber-200 border-amber-400/60', ic: '⚡', label: 'PRE-LODR' },
                            MEDIA_EARLY:   { cls: 'bg-sky-500/15 text-sky-200 border-sky-400/60',       ic: '📰', label: 'MEDIA' },
                            MEDIA_CONFIRM: { cls: 'bg-sky-500/10 text-sky-300/80 border-sky-500/40',    ic: '📰', label: 'MEDIA' },
                            MEDIA_LATE:    { cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/40',    ic: '📰', label: 'MEDIA' },
                          }[tier];
                          const tip =
                            tier === 'PRE_LODR'      ? `⚡ Genuine lead — this symbol was flagged ${(leadH/24).toFixed(1)}d before its BSE filing by ${(r.pre_lodr_sources || []).join(', ')}. Real information edge.`
                            : tier === 'MEDIA_EARLY' ? `${(r.pre_lodr_sources || []).join(', ')} published ${leadH.toFixed(1)}h before BSE — small but real lead.`
                            : tier === 'MEDIA_CONFIRM' ? `${(r.pre_lodr_sources || []).join(', ')} article within 6h of BSE — cross-verification signal.`
                            : `${(r.pre_lodr_sources || []).join(', ')} article lagged BSE — useful only for enrichment/context.`;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${style.cls}`}
                              title={tip}
                              data-testid={`leaderboard-pre-lodr-${r.symbol}`}
                            >
                              <span className="leading-none">{style.ic}</span>
                              {style.label}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">
                        {r.company_name}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center hidden md:table-cell">
                      {r.sector_tailwind ? (
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${tailwindTone(r.sector_tailwind)}`}>
                          {r.sector_tailwind}
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-[10px]">—</span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${pctTone(r.cum_pct_ttm)}`}>
                      {fmtPct(r.cum_pct_ttm)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-foreground">
                      {fmtCr(r.total_order_value_cr)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-300">
                      {r.n_orders}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.latest_verdict ? (
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${verdictTone(r.latest_verdict)}`}>
                          {r.latest_verdict}
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-[10px]">—</span>
                      )}
                    </td>
                    {sortBy === 'best' && (
                      <td className="px-3 py-2 text-right font-mono text-sky-300 font-semibold">
                        {r.conviction_score?.toFixed?.(0) ?? '—'}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right font-mono text-zinc-400 hidden md:table-cell">
                      {fmtCr(r.ttm_revenue_cr)}
                    </td>
                    <td className="px-3 py-2 hidden lg:table-cell">
                      <div className="text-[10px] text-zinc-500 font-mono">
                        {(r.latest_date || '').slice(0, 10)}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-zinc-500">
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <FilingsRow symbol={r.symbol} windowDays={windowDays} />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Footer explainer ── */}
      <div className="text-[11px] text-muted-foreground leading-relaxed border-t border-border/30 pt-3">
        <p>
          <strong className="text-foreground">How to read this:</strong>{' '}
          Companies at the top have booked new orders worth a large fraction
          of their trailing-12-month revenue in the selected window — a
          strong forward-earnings signal when combined with a BUY verdict
          on individual rows. Very high %TTM values (&gt; 200%) usually
          reflect long-execution contracts (multi-year railways, defence,
          EPC) — check the constituent filings and Deep Analysis for
          execution period before sizing up.
        </p>
      </div>
    </div>
  );
};

export default OrderBookLeaderboardTab;
